import { IdConta, IdInbox, IdNovidade } from "@/services/common-types";
import { GenericServiceBase } from "@/services/generic-service";

export interface CreateStorageNovidadeDto extends BaseUpload {
  id?: string
  idNovidade: IdNovidade
  file: File
}

export interface CreateStorageInboxDto extends BaseUpload {
  id?: string
  idInbox: IdInbox
  file: File
}

export interface CreateStorageTemplateDto extends BaseUpload {
  id?: string
  idConta: IdConta
  file: File
}

export interface ResponseStorage extends BaseUpload {
  id: string
  path: string
  filename: string
  type: 'AWS' | 'LOCAL'
  del: number
}

export interface CreateStorageChatInternoDto extends BaseUpload {
  id?: string
  file: File
}

export interface CreateStorageAvatarDto extends BaseUpload {
  usuarioId: string;
  id?: string;
  file: File;
}

export interface BaseUpload {
  file: File;
}

export interface CreateStorageContatoDto extends BaseUpload {
  contatoId: string;
  codigo: string;
  id?: string;
  file: File;
}

export const MAX_FILE_SIZE_MB = 150;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

export class FileTooLargeError extends Error {
  constructor(file: File, maxMb: number) {
    super(`O arquivo "${file.name}" ultrapassa o tamanho limite (${maxMb}MB).`);
    this.name = 'FileTooLargeError';
  }
}

export class StorageService extends GenericServiceBase {
  constructor() { super('storage') }

  areAllFilesAllowed(files: File[]): boolean {
    return files.every(file => this.isFileSizeAllowed(file));
  }

  getOversizedFiles(files: File[]): File[] {
    return files.filter(file => !this.isFileSizeAllowed(file));
  }

  isFileSizeAllowed(file: File) {
    return file.size <= MAX_FILE_SIZE_BYTES;
  }

  private assertFileSize(file: File) {
    if (this.isFileSizeAllowed(file)) return;

    throw new FileTooLargeError(file, MAX_FILE_SIZE_MB);
  }

  private async upload(path: string, baseUpload: BaseUpload): Promise<ResponseStorage> {
    this.assertFileSize(baseUpload.file);
    return await this.f.post(path, baseUpload, { formdata: true, jsonPayload: false }) as ResponseStorage;
  }

  async uploadInboxFile(createStorage: CreateStorageInboxDto): Promise<ResponseStorage> {
    return await this.upload('/upload/inbox', createStorage);
  }

  async uploadAgendamentoFile(createStorage: CreateStorageInboxDto): Promise<ResponseStorage> {
    return await this.upload('/upload/agendamento', createStorage);
  }

  async uploadTemplateFile(createStorage: CreateStorageTemplateDto): Promise<ResponseStorage> {
    return await this.upload('/upload/template', createStorage);
  }

  async uploadFileNovidade(createStorage: CreateStorageNovidadeDto): Promise<ResponseStorage> {
    return await this.upload('/upload/novidades', createStorage);
  }


  async uploadChatInternoFile(createStorage: CreateStorageChatInternoDto): Promise<ResponseStorage> {
    return await this.upload('/upload/chat-interno', createStorage);
  }

  async uploadAvatarFile(createStorage: CreateStorageAvatarDto): Promise<ResponseStorage> {
    return await this.upload('/upload/avatar', createStorage);
  }

  async uploadContatoFile(createStorage: CreateStorageContatoDto): Promise<ResponseStorage> {
    return await this.upload('/upload/contato', createStorage);
  }
}
